import theme from '@astro-ui/theme'
import Box from '../Box/Box'
import Icon, { IconPropsType } from '../Icon/Icon'

export type LabelColorType =
  | 'primary'
  | 'secondary'
  | 'red'
  | 'orange'
  | 'green'
  | 'blue'
  | 'grey'
  | 'yellow'
  | 'cyan'
  | 'purple'
  | 'magenta'

export type LabelSizeType = 'small' | 'standard' | 'tiny'

type LabelColorConfigType = {
  backgroundColor: string
  foregroundColor: string
  filledBackgroundColor: string
}
type LabelSizeConfigType = {
  horizontalPadding: string
  verticalPadding: string
  iconSize: number
  fontWeight: 400 | 700
}
type LabelIconPropsType =
  | { leadingIcon?: IconPropsType['icon']; trailingIcon?: never }
  | { leadingIcon?: never; trailingIcon?: IconPropsType['icon'] }

const LABEL_COLOR_CONFIG: Record<LabelColorType, LabelColorConfigType> = {
  primary: {
    backgroundColor: theme.palette.bgColor.lightPrimary,
    foregroundColor: theme.palette.textColor.link,
    filledBackgroundColor: theme.palette.bgColor.blue,
  },
  secondary: {
    backgroundColor: theme.palette.bgColor.lightSecondary,
    foregroundColor: theme.palette.textColor.linkSecondary,
    filledBackgroundColor: theme.palette.bgColor.secondary,
  },
  red: {
    backgroundColor: theme.palette.bgColor.lightRed,
    foregroundColor: theme.palette.textColor.error,
    filledBackgroundColor: theme.palette.bgColor.red,
  },
  orange: {
    backgroundColor: theme.palette.bgColor.lightOrange,
    foregroundColor: theme.palette.textColor.warning,
    filledBackgroundColor: theme.palette.bgColor.orange,
  },
  green: {
    backgroundColor: theme.palette.bgColor.lightGreen,
    foregroundColor: theme.palette.textColor.sucess,
    filledBackgroundColor: theme.palette.bgColor.green,
  },
  blue: {
    backgroundColor: theme.palette.bgColor.lightBlue,
    foregroundColor: theme.palette.textColor.link,
    filledBackgroundColor: theme.palette.bgColor.blue,
  },
  grey: {
    backgroundColor: theme.palette.bgColor.lightGrey,
    foregroundColor: '#475569',
    filledBackgroundColor: theme.palette.bgColor.grey,
  },
  yellow: {
    backgroundColor: theme.palette.bgColor.lightYellow,
    foregroundColor: theme.palette.textColor.yellow,
    filledBackgroundColor: theme.palette.bgColor.yellow,
  },
  cyan: {
    backgroundColor: theme.palette.bgColor.lightCyan,
    foregroundColor: theme.palette.textColor.cyan,
    filledBackgroundColor: theme.palette.bgColor.cyan,
  },
  purple: {
    backgroundColor: theme.palette.bgColor.lightPurple,
    foregroundColor: theme.palette.textColor.purple,
    filledBackgroundColor: theme.palette.bgColor.purple,
  },
  magenta: {
    backgroundColor: theme.palette.bgColor.lightMagenta,
    foregroundColor: theme.palette.textColor.magenta,
    filledBackgroundColor: theme.palette.bgColor.magenta,
  },
}

const LABEL_SIZE_CONFIG: Record<LabelSizeType, LabelSizeConfigType> = {
  small: { horizontalPadding: '4px', verticalPadding: '2px', iconSize: 14, fontWeight: 700 },
  standard: { horizontalPadding: '8px', verticalPadding: '4px', iconSize: 16, fontWeight: 700 },
  tiny: { horizontalPadding: '4px', verticalPadding: '2px', iconSize: 12, fontWeight: 400 },
}

export type LabelPropsType = {
  label: string
  size?: LabelSizeType
  color?: LabelColorType
  filled?: boolean
} & LabelIconPropsType

const Label = ({
  label,
  size = 'standard',
  color = 'grey',
  filled = false,
  leadingIcon,
  trailingIcon,
}: LabelPropsType) => {
  const colorConfig = LABEL_COLOR_CONFIG[color]
  const sizeConfig = LABEL_SIZE_CONFIG[size]
  const backgroundColor = filled ? colorConfig.filledBackgroundColor : colorConfig.backgroundColor
  const foregroundColor = filled ? theme.palette.neutral['neutral-1'] : colorConfig.foregroundColor

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      gap="2px"
      px={sizeConfig.horizontalPadding}
      py={sizeConfig.verticalPadding}
      borderRadius="4px"
      bg={backgroundColor}
      color={foregroundColor}
      style={{ whiteSpace: 'nowrap' }}
    >
      {leadingIcon && (
        <span aria-hidden="true">
          <Icon icon={leadingIcon} size={sizeConfig.iconSize} color={foregroundColor} />
        </span>
      )}
      <span
        style={{
          color: foregroundColor,
          fontFamily: theme.typography.fontFamily,
          fontSize: '10px',
          fontWeight: sizeConfig.fontWeight,
          lineHeight: '12px',
        }}
      >
        {label}
      </span>
      {trailingIcon && (
        <span aria-hidden="true">
          <Icon icon={trailingIcon} size={sizeConfig.iconSize} color={foregroundColor} />
        </span>
      )}
    </Box>
  )
}

export default Label
